import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, ArrowLeftRight, Store, Calendar, CreditCard, Loader2 } from "lucide-react";
import { api, apiError, fileUrl } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { StatusBadge, money, shortDate } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const ACTION_LABELS = { self_added: "Self-added", issued: "Issued", claimed: "Claimed", transferred: "Transferred" };

export default function ReceiptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transferEmail, setTransferEmail] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: r, isLoading, refetch } = useQuery({
    queryKey: ["receipt", id],
    queryFn: async () => (await api.get(`/receipts/${id}`)).data,
  });

  const doTransfer = async () => {
    if (!transferEmail) return;
    setBusy(true);
    try {
      await api.post("/transfers", { receipt_id: id, to_email: transferEmail });
      toast.success(`Transfer sent to ${transferEmail}`);
      setTransferOpen(false); setTransferEmail("");
      refetch();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  const proofUrl = r ? `${window.location.origin}/wallet/receipt/${r.id}` : "";

  return (
    <AppLayout>
      <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {isLoading || !r ? (
        <div className="grid gap-6 lg:grid-cols-3"><Skeleton className="h-96 lg:col-span-2" /><Skeleton className="h-96" /></div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-border bg-card p-7">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2.5">
                    {r.store_logo_path && <img src={fileUrl(r.store_logo_path)} alt="" className="h-8 w-8 rounded-lg object-cover" data-testid="detail-store-logo" />}
                    <p className="text-[11px] font-bold uppercase tracking-widest text-brand">{r.store_name}</p>
                  </div>
                  <h1 className="mt-1 font-heading text-3xl font-black">{money(r.total)}</h1>
                  <p className="text-sm text-muted-foreground">{shortDate(r.purchase_date)}</p>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  {r.status === "self_added" && <StatusBadge status="self_added" />}
                  {r.warranty_status && <StatusBadge status={r.warranty_status} />}
                </div>
              </div>

              <div className="mt-6 divide-y divide-border rounded-xl border border-border">
                {r.items?.map((i, idx) => (
                  <div key={idx} className="flex items-center justify-between px-4 py-3" data-testid={`detail-item-${idx}`}>
                    <span className="font-medium">{i.qty}× {i.name}</span>
                    <span className="font-mono tabular-nums">{money(i.price * i.qty)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between px-4 py-3 font-bold">
                  <span>Total {r.tax ? `(incl. ${money(r.tax)} GST)` : ""}</span>
                  <span className="font-mono tabular-nums">{money(r.total)}</span>
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <Info icon={Store} label="Store" value={r.store_name} />
                <Info icon={CreditCard} label="Payment" value={r.payment_method || "—"} />
                <Info icon={Calendar} label="Purchased" value={shortDate(r.purchase_date)} />
              </div>

              {r.warranty_expiry && (
                <div className="mt-6 flex items-center gap-3 rounded-xl bg-brand-muted p-4">
                  <ShieldCheck className="h-6 w-6 text-brand" />
                  <div>
                    <p className="font-semibold">Warranty until {shortDate(r.warranty_expiry)}</p>
                    <p className="text-xs text-muted-foreground">{r.warranty_months} month cover from purchase</p>
                  </div>
                </div>
              )}
            </div>

            {r.history?.length > 0 && (
              <div className="mt-6 rounded-2xl border border-border bg-card p-6">
                <h3 className="mb-4 font-heading font-bold">Ownership history</h3>
                <div className="space-y-3">
                  {r.history.map((h, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      <span className="h-2 w-2 rounded-full bg-brand" />
                      <span className="font-semibold">{ACTION_LABELS[h.action] || h.action}</span>
                      {h.from && <span className="text-muted-foreground">from {h.from}</span>}
                      <span className="ml-auto text-xs text-muted-foreground">{shortDate(h.at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="sticky top-24 space-y-4">
              <div className="rounded-2xl border border-border bg-card p-6 text-center">
                <p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Proof QR</p>
                <div className="mx-auto w-fit rounded-xl border border-border bg-white p-3">
                  <QRCodeCanvas value={proofUrl} size={150} fgColor="#5C24FF" />
                </div>
                <p className="mt-3 font-mono text-sm font-bold tracking-widest">{r.claim_code}</p>
              </div>

              <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full" data-testid="transfer-open-btn"><ArrowLeftRight className="mr-2 h-4 w-4" />Transfer ownership</Button>
                </DialogTrigger>
                <DialogContent data-testid="transfer-dialog">
                  <DialogHeader><DialogTitle className="font-heading">Transfer ownership</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground">Send this receipt and its warranty to another person. They'll accept it from their account.</p>
                  <div className="mt-4">
                    <Label htmlFor="temail">Recipient email</Label>
                    <Input id="temail" type="email" value={transferEmail} onChange={(e) => setTransferEmail(e.target.value)} placeholder="newowner@example.com" className="mt-1.5" data-testid="transfer-email-input" />
                  </div>
                  <Button onClick={doTransfer} disabled={busy} className="mt-4 w-full bg-brand text-white hover:bg-brand-hover" data-testid="transfer-submit-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send transfer"}
                  </Button>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-secondary p-4">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="truncate text-sm font-semibold">{value}</p>
    </div>
  );
}
